import React, { useEffect, useRef, useState } from 'react';
import { StyledPdfViewer, StyledPdfViewerWrapper } from './styles';
import * as pdfjsLib from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.min?url';

// Initialize PDF.js with proper font configuration
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// Configure PDF.js viewer options
const pdfjsVersion = '4.8.69';
const viewerOptions = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/standard_fonts/`,
};

// Function to convert S3 URL to blob URL
const createBlobUrlFromS3 = async (s3Url) => {
  try {
    const response = await fetch(s3Url);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  } catch (error) {
    throw error;
  }
};

export const PDFViewer = ({ fileUrl, pdfUrl, showAllPage, onDocumentComplete }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1.0);
  const [pdf, setPdf] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use either fileUrl or pdfUrl
  const sourceUrl = fileUrl || pdfUrl;

  const renderPages = async (pdfDoc) => {
    if (!containerRef.current || isRendering) {
      return;
    }

    try {
      setIsRendering(true);
      // Clear previous content
      containerRef.current.innerHTML = '';

      // Determine which pages to render
      const startPage = showAllPage ? 1 : 1;
      const endPage = showAllPage ? pdfDoc.numPages : 1;

      // Render pages
      for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.id = `page-${pageNum}`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto 1rem auto';
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
        canvas.style.width = 'auto';

        if (containerRef.current && context) {
          containerRef.current.appendChild(canvas);

          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };

          await page.render(renderContext).promise;
        }
      }
    } catch (error) {
      setError(`Error rendering PDF: ${error.message}`);
    } finally {
      setIsRendering(false);
    }
  };

  // Effect to create blob URL from S3 URL
  useEffect(() => {
    if (!sourceUrl) {
      return;
    }

    const convertToBlob = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const newBlobUrl = await createBlobUrlFromS3(sourceUrl);
        setBlobUrl(newBlobUrl);
      } catch (error) {
        setError(`Failed to load PDF: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    convertToBlob();

    // Cleanup function to revoke blob URL
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [sourceUrl]);

  useEffect(() => {
    if (!blobUrl || !containerRef.current) {
      return;
    }

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: blobUrl,
          ...viewerOptions,
        });
        const pdfDoc = await loadingTask.promise;
        setPdf(pdfDoc);

        // Call onDocumentComplete when PDF is loaded
        if (onDocumentComplete) {
          onDocumentComplete();
        }

        // Render pages
        await renderPages(pdfDoc);
      } catch (error) {
        setError(`Error loading PDF: ${error.message}`);
      }
    };

    loadPdf();

    // Cleanup function
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [blobUrl, onDocumentComplete]);

  // Re-render pages when scale changes
  useEffect(() => {
    if (pdf && !isRendering) {
      renderPages(pdf);
    }
  }, [scale, pdf]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = async () => {
    if (!sourceUrl) return;

    try {
      const response = await fetch(sourceUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {}
  };

  if (!sourceUrl) {
    return (
      <StyledPdfViewerWrapper>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'orange' }}>
          No PDF URL provided (fileUrl or pdfUrl prop required)
        </div>
      </StyledPdfViewerWrapper>
    );
  }

  if (isLoading) {
    return (
      <StyledPdfViewerWrapper>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading PDF...</div>
      </StyledPdfViewerWrapper>
    );
  }

  if (error) {
    return (
      <StyledPdfViewerWrapper>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>{error}</div>
      </StyledPdfViewerWrapper>
    );
  }

  return (
    <StyledPdfViewerWrapper>
      <StyledPdfViewer ref={containerRef} />
    </StyledPdfViewerWrapper>
  );
};
